/**
 * Admin V3 — shared visual tokens
 *
 * SOURCE OF TRUTH: frontend/src/pages/AdminDashboard.js (legacy).
 *
 * Helpers + component patterns are duplicated here (not imported) to keep
 * AdminDashboard.js completely untouched per owner directive. Visual fidelity
 * is guaranteed by literal class-string equivalence — every Tailwind string
 * in this module appears identically in AdminDashboard.js or the proven
 * components/adminv2/legacyVisuals.js port (M1+M2A).
 *
 * Additional sidebar tokens (SIDEBAR_*) are NEW for V3 but use the same
 * legacy palette (slate/white/active=slate-900 white).
 */

import React from 'react';

/* ============================================================================ UTILITY HELPERS */

// Verbatim from AdminDashboard.js:63
export const cn = (...classes) => classes.filter(Boolean).join(' ');

// Verbatim from AdminDashboard.js:65-68
export const currency = (value) => {
  const n = Number(value || 0);
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
};

// Verbatim from AdminDashboard.js:70-73
export const shortAddress = (value = '', max = 26) => {
  if (!value) return '—';
  return value.length > max ? `${value.slice(0, max)}...` : value;
};

// F3 — readable multi-line client address for the Admin detail pages.
// Mirrors the commercial PDF rule (context._commercial_addr_html): break on
// newline / ';' / ',', trim each part, drop blanks, rejoin with '\n'.
// PRESENTATION ONLY — does not reorder, parse, or mutate the stored value; it
// only introduces line breaks. Render the result with the `whitespace-pre-line`
// class so the joined newlines display as separate lines.
export const formatAddressLines = (value) => {
  if (typeof value !== 'string') return value || '';
  const parts = value
    .replace(/[;,]/g, '\n')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.join('\n');
};

/* ============================================================================ COMPLIANCE STATE META (Phase 4 G5 — Plan §2.2 M-F-17) */

// Per Architecture V1 §DCA-2 (9-state lifecycle) + Plan §2.3.B (8-state compliance posture).
// Pill colors follow the bookingStatusMeta convention.
export const lifecycleStateMeta = {
  pending_backfill:     { labelKey: 'compliance.lifecycle.pending_backfill',     pill: 'bg-slate-100 text-slate-700 border border-slate-200',   line: 'bg-slate-400' },
  candidate:            { labelKey: 'compliance.lifecycle.candidate',            pill: 'bg-amber-100 text-amber-700 border border-amber-200',   line: 'bg-amber-400' },
  documents_submitted:  { labelKey: 'compliance.lifecycle.documents_submitted',  pill: 'bg-cyan-100 text-cyan-700 border border-cyan-200',      line: 'bg-cyan-400' },
  under_review:         { labelKey: 'compliance.lifecycle.under_review',         pill: 'bg-violet-100 text-violet-700 border border-violet-200', line: 'bg-violet-400' },
  approved:             { labelKey: 'compliance.lifecycle.approved',             pill: 'bg-blue-100 text-blue-700 border border-blue-200',      line: 'bg-blue-400' },
  active:               { labelKey: 'compliance.lifecycle.active',               pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200', line: 'bg-emerald-400' },
  active_ineligible:    { labelKey: 'compliance.lifecycle.active_ineligible',    pill: 'bg-orange-100 text-orange-700 border border-orange-200', line: 'bg-orange-400' },
  suspended:            { labelKey: 'compliance.lifecycle.suspended',            pill: 'bg-rose-100 text-rose-700 border border-rose-200',      line: 'bg-rose-400' },
  archived:             { labelKey: 'compliance.lifecycle.archived',             pill: 'bg-slate-200 text-slate-600 border border-slate-300',   line: 'bg-slate-500' },
};

// driver_compliance_profiles.compliance_state — 8 states (no ACTIVE)
export const complianceStateMeta = {
  pending_backfill:    lifecycleStateMeta.pending_backfill,
  candidate:           lifecycleStateMeta.candidate,
  documents_submitted: lifecycleStateMeta.documents_submitted,
  under_review:        lifecycleStateMeta.under_review,
  approved:            lifecycleStateMeta.approved,
  active_ineligible:   lifecycleStateMeta.active_ineligible,
  suspended:           lifecycleStateMeta.suspended,
  archived:            lifecycleStateMeta.archived,
};

// Verification status (driver_documents.verification_status)
export const verificationStatusMeta = {
  pending:  { labelKey: 'compliance.verif.pending',  pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  verified: { labelKey: 'compliance.verif.verified', pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  rejected: { labelKey: 'compliance.verif.rejected', pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  expired:  { labelKey: 'compliance.verif.expired',  pill: 'bg-orange-100 text-orange-700 border border-orange-200' },
};

// Severity badges (expiring-soon, expired, missing)
export const documentSeverityMeta = {
  expiring_soon: { labelKey: 'compliance.severity.expiring_soon', pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  expired:       { labelKey: 'compliance.severity.expired',       pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  missing:       { labelKey: 'compliance.severity.missing',       pill: 'bg-slate-100 text-slate-600 border border-slate-200' },
};

/* ============================================================================ BOOKING STATUS META */

// Verbatim from AdminDashboard.js:35-44
export const bookingStatusMeta = {
  pending:         { label: 'En attente',      pill: 'bg-amber-100 text-amber-700 border border-amber-200',     line: 'bg-amber-400' },
  pending_payment: { label: 'Attente paiement', pill: 'bg-orange-100 text-orange-700 border border-orange-200', line: 'bg-orange-400' },
  awaiting_schedule: { label: 'À planifier',    pill: 'bg-indigo-100 text-indigo-700 border border-indigo-200', line: 'bg-indigo-400' },
  confirmed:       { label: 'Confirmé',        pill: 'bg-blue-100 text-blue-700 border border-blue-200',        line: 'bg-blue-400' },
  assigned:        { label: 'Assigné',         pill: 'bg-violet-100 text-violet-700 border border-violet-200',  line: 'bg-violet-400' },
  accepted:        { label: 'Accepté',         pill: 'bg-cyan-100 text-cyan-700 border border-cyan-200',        line: 'bg-cyan-400' },
  driver_arriving: { label: 'En route',        pill: 'bg-sky-100 text-sky-700 border border-sky-200',           line: 'bg-sky-400' },
  in_progress:     { label: 'En cours',        pill: 'bg-teal-100 text-teal-700 border border-teal-200',        line: 'bg-teal-400' },
  completed:       { label: 'Terminé',         pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200', line: 'bg-emerald-400' },
  cancelled:       { label: 'Annulé',          pill: 'bg-rose-100 text-rose-700 border border-rose-200',        line: 'bg-rose-400' },
};

// Verbatim from AdminDashboard.js:46-57, extended (Phase 2.2 UX polish) with
// event names produced by atomic primitives + refund flow that legacy map omitted:
//   DRIVER_MANUAL_ASSIGNED / DRIVER_AUTO_ASSIGNED — atomic_assign_driver_to_booking
//   REFUND_PROCESSED — atomic_process_refund
export const timelineEventLabels = {
  BOOKING_CREATED:        { label: 'Réservation créée',                dot: 'bg-blue-500',    color: 'text-blue-700' },
  DRIVER_ASSIGNED:        { label: 'Chauffeur assigné',                dot: 'bg-violet-500',  color: 'text-violet-700' },
  DRIVER_MANUAL_ASSIGNED: { label: 'Chauffeur assigné manuellement',   dot: 'bg-violet-500',  color: 'text-violet-700' },
  DRIVER_AUTO_ASSIGNED:   { label: 'Chauffeur assigné automatiquement',dot: 'bg-violet-500',  color: 'text-violet-700' },
  DRIVER_UNASSIGNED:      { label: 'Chauffeur retiré',                 dot: 'bg-orange-500',  color: 'text-orange-700' },
  RIDE_ACKNOWLEDGED:      { label: 'Course confirmée',                 dot: 'bg-cyan-500',    color: 'text-cyan-700' },
  DRIVER_EN_ROUTE:        { label: 'Chauffeur en route',               dot: 'bg-amber-500',   color: 'text-amber-700' },
  DRIVER_ARRIVED:         { label: 'Chauffeur arrivé',                 dot: 'bg-lime-500',    color: 'text-lime-700' },
  RIDE_STARTED:           { label: 'Course démarrée',                  dot: 'bg-emerald-500', color: 'text-emerald-700' },
  RIDE_COMPLETED:         { label: 'Course terminée',                  dot: 'bg-green-600',   color: 'text-green-700' },
  RIDE_CANCELLED:         { label: 'Course annulée',                   dot: 'bg-rose-500',    color: 'text-rose-700' },
  BOOKING_CANCELLED:      { label: 'Réservation annulée',              dot: 'bg-rose-500',    color: 'text-rose-700' },
  PAYMENT_CAPTURED:       { label: 'Paiement reçu',                    dot: 'bg-emerald-500', color: 'text-emerald-700' },
  REFUND_PROCESSED:       { label: 'Remboursement effectué',           dot: 'bg-rose-500',    color: 'text-rose-700' },
  INCIDENT_REPORTED:      { label: 'Incident signalé',                 dot: 'bg-amber-500',   color: 'text-amber-700' },
};

/* ============================================================================ DRIVER STATUS META (Phase 2.3) */

// Operational status — palette extends legacy 'online/offline' pattern
// (emerald/slate from AdminDashboard.js:1179) with two new states
// (busy/suspended) that the legacy section card never surfaced.
// Backend canonical enum: available | busy | offline | suspended
// (backend/app/services/state_machine/driver_state_machine.py:21-31).
export const driverStatusMeta = {
  available: { label: 'Disponible',  pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  busy:      { label: 'En course',   pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  offline:   { label: 'Hors ligne',  pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
  suspended: { label: 'Suspendu',    pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
};

// Approval status — used in the V3 drivers list "Approbation" column.
// Backend enum: pending | approved | rejected (same file lines 34-41).
export const driverApprovalMeta = {
  approved: { label: 'Approuvé',   pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  pending:  { label: 'En attente', pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  rejected: { label: 'Rejeté',     pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
};

// Subscription pill — palette verbatim from AdminDashboard.js:1180.
export const driverSubscriptionMeta = {
  active:   { label: 'Abonné',     pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  inactive: { label: 'Non abonné', pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
};

/* ============================================================================ LIVE OPERATIONS META (Phase 2.5) */

// Live dispatch priority — derived server-side in admin_control_tower_v2.py:292-427.
// Owner ruling #10 (Phase 2.5): added in-scope.
export const livePriorityMeta = {
  HIGH:   { label: 'Haute',   pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  MEDIUM: { label: 'Moyenne', pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  LOW:    { label: 'Basse',   pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

// Anomaly types raised by the live dispatch monitor — backend emits flags like
// "unassigned_30min" / "no_progress_60min" in /admin/control-tower/live anomalies[].
// French labels are operator-friendly; unknown anomaly types fall back to a
// neutral slate pill displaying the raw key (owner ruling #4).
export const anomalyTypeMeta = {
  unassigned_30min:  { label: 'Non assigné > 30 min',   pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  no_progress_60min: { label: 'Aucun progrès > 60 min', pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
};

/* ============================================================================ OPERATOR ACTION META (Phase 2.6) */

// French labels + color palette for the 18 OperatorActionType enum values
// (backend: app/services/operations/operator_log.py:36-73, stored as snake_case).
// Palette is grouped by action domain so operators can scan the audit log
// for booking-related vs payment-related vs incident-related actions at a glance.
export const operatorActionTypeMeta = {
  // Booking (blue)
  cancel_booking:        { label: 'Annulation de réservation',        pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  redispatch:            { label: 'Redispatch',                       pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  manual_status_change:  { label: 'Changement de statut manuel',      pill: 'bg-blue-100 text-blue-700 border border-blue-200' },

  // Payment (emerald)
  retry_payment:         { label: 'Nouvel essai de paiement',         pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  refund_payment:        { label: 'Remboursement',                    pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },

  // Payout (emerald)
  trigger_payout:        { label: 'Versement déclenché',              pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  hold_payout:           { label: 'Versement suspendu',               pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  release_payout:        { label: 'Versement libéré',                 pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },

  // Event (cyan)
  reprocess_event:       { label: "Retraitement d'événement",         pill: 'bg-cyan-100 text-cyan-700 border border-cyan-200' },
  purge_dead_letter:     { label: 'Purge dead letter',                pill: 'bg-cyan-100 text-cyan-700 border border-cyan-200' },

  // Incident (amber)
  acknowledge_incident:  { label: 'Incident accusé',                  pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  resolve_incident:      { label: 'Incident résolu',                  pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  escalate_incident:     { label: 'Incident escaladé',                pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  update_incident:       { label: 'Incident mis à jour',              pill: 'bg-amber-100 text-amber-700 border border-amber-200' },

  // Driver (violet)
  reassign_driver:       { label: 'Réaffectation chauffeur',          pill: 'bg-violet-100 text-violet-700 border border-violet-200' },

  // Notification (blue)
  resend_notification:   { label: 'Notification renvoyée',            pill: 'bg-blue-100 text-blue-700 border border-blue-200' },

  // System (slate)
  refresh_read_models:   { label: 'Rafraîchir modèles de lecture',    pill: 'bg-slate-100 text-slate-700 border border-slate-200' },
  force_sync:            { label: 'Synchronisation forcée',           pill: 'bg-slate-100 text-slate-700 border border-slate-200' },

  // Sensitive view (rose)
  view_sensitive_data:   { label: 'Consultation données sensibles',   pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
};

/* ============================================================================ NOTIFICATION META (Phase 2.7) */

// Notification delivery status — backend NotificationStatus enum
// (app/services/notifications/notification_service.py:56-61).
export const notificationStatusMeta = {
  pending: { label: 'En attente', pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  sent:    { label: 'Envoyé',     pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  failed:  { label: 'Échec',      pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  skipped: { label: 'Ignoré',     pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

// Notification channel — SMS (Twilio) or Email (Resend).
export const notificationChannelMeta = {
  sms:   { label: 'SMS',    pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  email: { label: 'Email',  pill: 'bg-violet-100 text-violet-700 border border-violet-200' },
};

// Notification trigger type — 8 backend NotificationType enum values
// (notification_service.py:35-47, stored snake_case in to_dict()).
export const notificationTypeMeta = {
  payment_confirmed:       { label: 'Paiement confirmé' },
  driver_assigned:         { label: 'Chauffeur assigné' },
  driver_arriving:         { label: 'Chauffeur en route' },
  ride_completed:          { label: 'Course terminée' },
  booking_confirmation:    { label: 'Confirmation de réservation' },
  payment_receipt:         { label: 'Reçu de paiement' },
  refund_confirmation:     { label: 'Confirmation de remboursement' },
  driver_assignment_email: { label: "Notification d'assignation chauffeur" },
};

/* ============================================================================ SYSTEM HEALTH META (Phase 2.8) */

// Per-service status values emitted by /admin/control-tower/health/extended
// (admin_control_tower_v2.py:1099-1120). "mock" = service stub mode (acceptable
// in dev/test); "unavailable" = network/ping failure.
export const serviceStatusMeta = {
  healthy:     { label: 'Sain',         pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200', alert: false },
  unhealthy:   { label: 'Défaillant',   pill: 'bg-rose-100 text-rose-700 border border-rose-200',          alert: true  },
  unavailable: { label: 'Indisponible', pill: 'bg-rose-100 text-rose-700 border border-rose-200',          alert: true  },
  mock:        { label: 'Mode mock',    pill: 'bg-amber-100 text-amber-700 border border-amber-200',       alert: false },
};

// Overall platform status — emitted as "healthy" or "degraded" by the same
// endpoint (admin_control_tower_v2.py:1156-1161).
export const overallStatusMeta = {
  healthy:  { label: 'Plateforme saine',     pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200', alert: false },
  degraded: { label: 'Plateforme dégradée',  pill: 'bg-rose-100 text-rose-700 border border-rose-200',          alert: true  },
};

/* ============================================================================ INCIDENT META (Phase 2.9) */

// Incident lifecycle status — backend IncidentStatus enum
// (app/services/operations/incident_engine.py:67-73). The backend list
// endpoint stats only count open / in_progress / resolved; acknowledged
// and closed exist as filter values but aren't tallied in the stats card.
export const incidentStatusMeta = {
  open:         { label: 'Ouvert',     pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  acknowledged: { label: 'Reconnu',    pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  in_progress:  { label: 'En cours',   pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  resolved:     { label: 'Résolu',     pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  closed:       { label: 'Clôturé',    pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

// Incident severity — IncidentSeverity enum (incident_engine.py:59-64).
// Visual priority: critical=rose, high=amber, medium=blue, low=slate.
export const incidentSeverityMeta = {
  critical: { label: 'Critique', pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  high:     { label: 'Haute',    pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  medium:   { label: 'Moyenne',  pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  low:      { label: 'Faible',   pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

/* ============================================================================ PAYOUT STATUS META (Phase 2.15) */

// Payout status — defensive both-taxonomy dictionary.
//
// Production payout records may carry either of two taxonomies:
//   1. PayoutState (canonical, 6 values, payout_state_machine.py:36-43):
//      created, pending, released, paid, failed, reversed
//   2. PayoutStatus (legacy, 4 values, economy_types.py:26-30):
//      pending, released, paid_out, cancelled
//
// Both `paid` (canonical) and `paid_out` (legacy) mean "transferred to bank"
// and render with the same French label and color. Unknown values fall back
// to neutral slate pill.
//
// Color palette:
//   slate    = informational / cancelled (no operator action implied)
//   amber    = waiting for release window
//   blue     = released to wallet (intermediate state)
//   emerald  = terminal success (money sent)
//   rose     = terminal failure / reversal
export const payoutStatusMeta = {
  // PayoutState canonical (6)
  created:   { label: 'Créé',     pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
  pending:   { label: 'En attente', pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  released:  { label: 'Libéré',   pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  paid:      { label: 'Versé',    pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  failed:    { label: 'Échec',    pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  reversed:  { label: 'Annulé après versement', pill: 'bg-rose-100 text-rose-700 border border-rose-200' },

  // PayoutStatus legacy (additional values not in PayoutState)
  paid_out:  { label: 'Versé',    pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  cancelled: { label: 'Annulé',   pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

/* ============================================================================ WITHDRAWAL STATUS META (Phase 2.14) */

// Withdrawal status — backend WithdrawalStatus enum (driver_economy.py:72-92).
// 6 canonical values. The `reversed` value is a post-B-2 promotion of a
// previously-disk-only string; pre-B-2 rows on disk are valid as-is and must
// render correctly with no fallback warning.
export const withdrawalStatusMeta = {
  pending:    { label: 'En attente',  pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  processing: { label: 'En cours',    pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  completed:  { label: 'Versé',       pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  failed:     { label: 'Échec',       pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  reversed:   { label: 'Annulé après versement', pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  cancelled:  { label: 'Annulé',      pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

/* ============================================================================ LEDGER ENTRY META (Phase 2.13) */

// Wallet ledger entry types — defensive both-taxonomy dictionary.
//
// TWO backend taxonomies coexist (ongoing Layer 3.2 → 3.7 migration):
//   1. LedgerEntryType (legacy, 7 values, app/services/economy/wallet_ledger.py:49-57)
//   2. OperationType (canonical, 9 values, app/services/economy/economy_types.py:67-81)
//
// Entries on disk may carry either taxonomy. Render uses operation_type when
// present, falls back to entry_type, then to raw key (slate pill).
//
// Color semantics:
//   emerald  = money into driver (payout created / released)
//   blue     = money out of driver via withdrawal (reservation step)
//   rose     = reversal / failure (clawback)
//   amber    = manual intervention (adjustments) or freeze
//   slate    = informational state op (unfreeze)
export const ledgerEntryTypeMeta = {
  // Payout family
  payout_created:      { label: 'Versement créé',     pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  payout_released:     { label: 'Versement libéré',   pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  payout_reversed:     { label: 'Versement annulé',   pill: 'bg-rose-100 text-rose-700 border border-rose-200' },

  // Adjustments (manual operator intervention — audit trail required upstream)
  adjustment_credit:   { label: 'Ajustement crédit',  pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  adjustment_debit:    { label: 'Ajustement débit',   pill: 'bg-amber-100 text-amber-700 border border-amber-200' },

  // Withdrawals
  withdrawal:          { label: 'Retrait (legacy)',   pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  withdrawal_reserved: { label: 'Retrait réservé',    pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  withdrawal_failed:   { label: 'Retrait échoué',     pill: 'bg-rose-100 text-rose-700 border border-rose-200' },

  // State operations (operator hold / release)
  freeze:              { label: 'Gel',                pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  unfreeze:            { label: 'Dégel',              pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

/* ============================================================================ ENTERPRISE META (Phase 2.12) */

// Enterprise account status — backend EnterpriseResponse.status is a free-form
// string (schemas.py:275, default "active"). No strict enum exists; the
// values below match the most common operational states. Unknown values fall
// back to a neutral slate pill displaying the raw key.
export const enterpriseStatusMeta = {
  active:    { label: 'Actif',     pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  suspended: { label: 'Suspendu',  pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  inactive:  { label: 'Inactif',   pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

/* ============================================================================ PARTNER META (Phase 2.11) */

// Partner type — backend PartnerType enum (app/domain/partner/types.py:31-35).
export const partnerTypeMeta = {
  hotel:     { label: 'Hôtel',      pill: 'bg-violet-100 text-violet-700 border border-violet-200' },
  corporate: { label: 'Entreprise', pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  reseller:  { label: 'Revendeur',  pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
};

// Partner status — backend PartnerStatus enum (app/domain/partner/types.py:38-42).
export const partnerStatusMeta = {
  active:    { label: 'Actif',     pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  suspended: { label: 'Suspendu',  pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  pending:   { label: 'En attente',pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
};

/* ============================================================================ BILLING META (Phase 2.10) */

// Invoice lifecycle status — backend InvoiceStatus enum
// (app/services/billing/invoice_engine.py:45-52). 6 values.
export const invoiceStatusMeta = {
  draft:          { label: 'Brouillon',     pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
  issued:         { label: 'Émise',         pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  paid:           { label: 'Payée',         pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  partially_paid: { label: 'Partiellement payée', pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  overdue:        { label: 'En retard',     pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  voided:         { label: 'Annulée',       pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

// Avoir (credit note) fiscal states — draft -> issued; `issuing` is an internal
// transient (atomic-issue claim) that may briefly appear (avoir_engine.py).
export const avoirStatusMeta = {
  draft:   { label: 'Brouillon', pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
  issuing: { label: 'Émission…', pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  issued:  { label: 'Émis',      pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
};

// Devis (quotation) commercial states (devis_engine.py). `converting` is an
// internal transient (atomic conversion claim).
export const devisStatusMeta = {
  draft:      { label: 'Brouillon',   pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
  sent:       { label: 'Envoyé',      pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  accepted:   { label: 'Accepté',     pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  converting: { label: 'Conversion…', pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  converted:  { label: 'Converti',    pill: 'bg-teal-100 text-teal-700 border border-teal-200' },
  rejected:   { label: 'Refusé',      pill: 'bg-rose-100 text-rose-700 border border-rose-200' },
  expired:    { label: 'Expiré',      pill: 'bg-orange-100 text-orange-700 border border-orange-200' },
  cancelled:  { label: 'Annulé',      pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
  superseded: { label: 'Remplacé',    pill: 'bg-violet-100 text-violet-700 border border-violet-200' },
};

// Bon de commande (order confirmation) commercial states (bdc_engine.py).
export const bdcStatusMeta = {
  draft:      { label: 'Brouillon',   pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
  issued:     { label: 'Émis',        pill: 'bg-blue-100 text-blue-700 border border-blue-200' },
  signed:     { label: 'Signé',       pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  converting: { label: 'Conversion…', pill: 'bg-amber-100 text-amber-700 border border-amber-200' },
  fulfilled:  { label: 'Honoré',      pill: 'bg-teal-100 text-teal-700 border border-teal-200' },
  cancelled:  { label: 'Annulé',      pill: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

// Incident type — TWO backend taxonomies coexist (documented drift):
//
//   1. ROUTE taxonomy (admin_control_tower_v2.py:50-58) — 8 values that the
//      GET /admin/control-tower/incidents endpoint validates against its
//      `incident_type` query param. THESE are the only values the route will
//      accept as a filter (anything else returns HTTP 422). The Phase 2.9
//      filter dropdown exposes only these.
//
//   2. SERVICE taxonomy (incident_engine.py:48-56) — 7 values used by the
//      auto-detection incident engine when raising incidents. Stored
//      incidents in the `incidents` collection may carry EITHER taxonomy's
//      values depending on creation path.
//
// We keep BOTH taxonomies here so the list table can label any stored
// incident correctly (defensive forward-compat). Filter dropdowns only
// expose the ROUTE taxonomy (single source of accepted filter values).
export const incidentTypeMeta = {
  // ROUTE taxonomy (8) — accepted by the filter query param
  delay:                    { label: 'Retard' },
  no_driver:                { label: 'Pas de chauffeur' },
  payment_issue:            { label: 'Problème de paiement' },
  customer_complaint:       { label: 'Plainte client' },
  vehicle_issue:            { label: 'Problème véhicule' },
  no_show:                  { label: 'Client absent' },
  accident:                 { label: 'Accident' },
  other:                    { label: 'Autre' },

  // SERVICE taxonomy (7) — emitted by auto-detection engine; preserved here
  // so the table renders these values with French labels even though the
  // filter dropdown cannot select them.
  stuck_booking:            { label: 'Réservation bloquée' },
  payment_failure:          { label: 'Échec de paiement' },
  dispatch_failure:         { label: 'Échec de répartition' },
  payout_delay:             { label: 'Retard de versement' },
  event_processing_failure: { label: "Échec de traitement d'événement" },
  driver_no_show:           { label: 'Chauffeur absent' },
  consistency_violation:    { label: 'Violation de cohérence' },
};

/* ============================================================================ SHARED UI */

// Verbatim from AdminDashboard.js:100-103
export const StatusPill = ({ status }) => {
  const meta = bookingStatusMeta[status] || { label: status || '—', pill: 'bg-slate-100 text-slate-700 border border-slate-200' };
  return <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold', meta.pill)}>{meta.label}</span>;
};

// Generic small pill driven by a passed meta object (Phase 2.3 — drivers).
// Mirrors StatusPill but decouples from bookingStatusMeta so future modules
// can reuse it with any {label, pill} meta map.
export const SmallPill = ({ meta }) => {
  if (!meta) return <span className="text-slate-400">—</span>;
  return <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', meta.pill)}>{meta.label}</span>;
};

// SC-PR-7 — Customer-widget tier badge for admin booking views.
// Maps the persisted ``bookings.service_type`` field (eco / business /
// van, written since SC-PR-5 / SC-PR-6) to a coloured pill.
// G3.E: PRIORITY removed from customer-widget tier vocabulary.
// Unknown / null / "standard" / "priority" (legacy/removed) → muted
// em-dash so legacy bookings (the ~25 pre-SC-PR-5 rows that have no
// tier stored) and any future non-customer engine path render
// gracefully without crashing.
//
// Used by BookingsListTable (one cell per row) and BookingTimelineModal
// (one badge in the modal header).
const TIER_META = {
  eco:       { pill: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  business:  { pill: 'bg-slate-100  text-slate-700   border border-slate-200'   },
  van:       { pill: 'bg-amber-50   text-amber-700   border border-amber-200'   },
};

export const TierBadge = ({ tier, label }) => {
  const key = typeof tier === 'string' ? tier.toLowerCase() : null;
  const meta = key && TIER_META[key];
  if (!meta) return <span className="text-slate-400">—</span>;
  const display = label || key.toUpperCase();
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold', meta.pill)}>
      {display}
    </span>
  );
};

// SC-PR-11 — Refund-policy recommendation class badge.
// Maps the engine's RecommendationClass enum (from SC-PR-10
// canonical_policy.py) to a coloured pill. Visual treatment chosen so
// each classification reads immediately:
//   FULL_REFUND           → emerald (favorable to customer)
//   PARTIAL_REFUND        → amber   (caution / fee applies)
//   NO_REFUND             → red     (customer at fault / billed)
//   NOT_APPLICABLE        → slate   (no payment to refund / already done)
//   MANUAL_REVIEW_REQUIRED→ purple  (engine declined — operator decides)
//
// Used by RefundGuidanceModal (advisory-only consumer of
// GET /admin/bookings/{id}/refund-recommendation). The badge itself
// performs no action; it is pure presentation.
const RECOMMENDATION_CLASS_META = {
  FULL_REFUND:            { pill: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  PARTIAL_REFUND:         { pill: 'bg-amber-50   text-amber-700   border border-amber-200'   },
  NO_REFUND:              { pill: 'bg-red-50     text-red-700     border border-red-200'     },
  // SC-PR-11A: strengthened from slate-100/600/200 to zinc-200/800/400 so
  // the NOT_APPLICABLE pill is visually distinct from the empty-placeholder
  // box (which uses slate-50/200 styling). Tone stays neutral grey to keep
  // the "non-actionable / informational" signal — not promoted to colour.
  NOT_APPLICABLE:         { pill: 'bg-zinc-200   text-zinc-800    border border-zinc-400'    },
  MANUAL_REVIEW_REQUIRED: { pill: 'bg-purple-50  text-purple-700  border border-purple-200'  },
};

export const RecommendationClassBadge = ({ classification, label }) => {
  const key = typeof classification === 'string' ? classification : null;
  const meta = key && RECOMMENDATION_CLASS_META[key];
  if (!meta) return <span className="text-slate-400">—</span>;
  const display = label || key;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold', meta.pill)}>
      {display}
    </span>
  );
};

// Verbatim from AdminDashboard.js:105-118
export const SectionCard = ({ title, icon: Icon, iconClass = 'bg-slate-100 text-slate-600', action, children }) => (
  <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_8px_30px_rgba(15,23,42,0.06)]">
    <div className="mb-5 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', iconClass)}>
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="text-[18px] font-semibold text-slate-900">{title}</h3>
      </div>
      {action && <div>{action}</div>}
    </div>
    {children}
  </div>
);

// Verbatim from AdminDashboard.js:120-127
export const EmptyState = ({ icon: Icon, title, desc, action }) => (
  <div className="flex flex-col items-center justify-center py-14 text-center">
    <div className="mb-4 opacity-30"><Icon className="h-12 w-12 text-slate-400" /></div>
    <p className="text-[15px] font-semibold text-slate-700">{title}</p>
    {desc && <p className="mt-1 text-sm text-slate-400 max-w-xs">{desc}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

/* ============================================================================ SIDEBAR TOKENS (new for V3, legacy palette) */

// Sidebar container: white card with right border (slate-200), full-height column.
export const SIDEBAR_CONTAINER_CLASSES =
  'w-60 min-h-screen bg-white border-r border-slate-200 flex-shrink-0';

// Sidebar group label (e.g., "OPÉRATIONS"): muted slate-400, uppercase, tracked.
// Pattern matches legacy "Aucune réservation" muted-label convention.
export const SIDEBAR_GROUP_LABEL_CLASSES =
  'px-4 pt-6 pb-2 text-[11px] uppercase tracking-wider text-slate-400';

// Sidebar item (default): legacy slate-600 + hover slate-50.
export const SIDEBAR_ITEM_CLASSES =
  'flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors';

// Sidebar item (active): legacy active-tab style (bg-slate-900 + white text).
export const SIDEBAR_ITEM_ACTIVE_CLASSES =
  'bg-slate-900 text-white';

// Sidebar sub-item (nested under a parent): same as item + extra indent.
export const SIDEBAR_SUBITEM_CLASSES =
  'flex items-center gap-2.5 pl-10 pr-4 py-2 text-[13px] font-medium text-slate-500 hover:bg-slate-50 transition-colors';

// Sidebar sub-item (active).
export const SIDEBAR_SUBITEM_ACTIVE_CLASSES =
  'bg-slate-900 text-white';
